import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { MIN_SEARCH_LENGTH, type SearchResult, useGlobalSearch } from '@/models/search'

interface ResultGroup {
  model: string
  items: SearchResult[]
}

function groupByModel(results: SearchResult[]): ResultGroup[] {
  const groups: ResultGroup[] = []
  for (const result of results) {
    let group = groups.find((g) => g.model === result.model)
    if (!group) {
      group = { model: result.model, items: [] }
      groups.push(group)
    }
    group.items.push(result)
  }
  return groups
}

export function GlobalSearch() {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const { data: results = [], isFetching } = useGlobalSearch(debouncedQuery)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const trimmedQuery = query.trim()
  const active = open && trimmedQuery.length >= MIN_SEARCH_LENGTH
  // Rows render only once the debounce + fetch have settled on the *current*
  // query, so a stale result for the previous term can't be shown or selected.
  const settled = !isFetching && debouncedQuery.trim() === trimmedQuery
  const groups = groupByModel(results)
  const isMac = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/i.test(navigator.userAgent)
  const shortcutHint = isMac ? '⌘K' : 'Ctrl K'

  function close() {
    setOpen(false)
    setQuery('')
    setDebouncedQuery('')
  }

  function handleSelect(result: SearchResult) {
    if (!result.link) return
    navigate(result.link)
    close()
  }

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-200',
          active ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={close}
        aria-hidden
      />
      {/* min-w-0: without it the input's intrinsic width wins and the nav overflows the phone. */}
      <div className="relative z-50 w-full max-w-md min-w-0">
        <Command
          shouldFilter={false}
          className="overflow-visible bg-transparent"
          onKeyDown={(e) => {
            if (e.key === 'Escape') close()
          }}
        >
          <div className="bg-surface relative rounded-md border shadow-none **:data-[slot=command-input-wrapper]:border-b-0">
            <CommandInput
              ref={inputRef}
              placeholder="Search organizations, leads, drivers…"
              value={query}
              onValueChange={(value) => {
                setQuery(value)
                setOpen(true)
              }}
              onFocus={() => {
                setFocused(true)
                setOpen(true)
              }}
              onBlur={() => setFocused(false)}
              className="pr-14"
            />
            {!focused && !query && (
              <kbd className="text-muted-foreground bg-muted pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 rounded border px-1.5 py-0.5 font-mono text-xs">
                {shortcutHint}
              </kbd>
            )}
          </div>
          {active && (
            <CommandList className="bg-popover absolute top-full mt-1 w-full rounded-md border shadow-md">
              {!settled && <div className="text-muted-foreground py-6 text-center text-sm">Searching…</div>}
              {settled && groups.length === 0 && <CommandEmpty>No results found</CommandEmpty>}
              {settled &&
                groups.map((group) => (
                  <CommandGroup
                    key={group.model}
                    heading={group.model === 'Activity' ? 'Activities' : group.model}
                  >
                    {group.items.map((result) => (
                      <CommandItem
                        key={`${result.model}-${result.id}`}
                        value={`${result.model}-${result.id}`}
                        onSelect={() => handleSelect(result)}
                        disabled={!result.link}
                        className={result.link ? 'cursor-pointer' : ''}
                      >
                        <div className="flex min-w-0 flex-1 flex-col">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">{result.headline}</span>
                            {result.context && (
                              <span className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-xs">
                                {result.context}
                              </span>
                            )}
                          </div>
                          {result.text && (
                            <span className="text-muted-foreground truncate text-xs">{result.text}</span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
            </CommandList>
          )}
        </Command>
      </div>
    </>
  )
}
